import React, { FC, useCallback, useEffect, useRef, useState, RefObject, useMemo, memo } from 'react';
import { createEditor, Descendant, Editor, Transforms, Element as SlateElement, Node, Text } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, Slate, withReact, ReactEditor } from 'slate-react';
import { useAppSelector } from '../../app/hooks';

// Plugins
import withLinks from './plugins/withLinks';
import withLists from './plugins/withLists';
import withTables from './plugins/withTable';
import withEmbeds from './plugins/withEmbeds';
import withEquation from './plugins/withEquation';
import withHtmlPaste from './plugins/withHtmlPaste';
import withExcelPaste from './plugins/withExcelPaste';

import Toolbar from './Toolbar/Toolbar';
import { getBlock, getMarked, toggleMark } from './utils/SlateUtilityFunctions';
import TextEditorShimmerLoader from './TextEditorShimmerLoader';

import styles from './TextEditor.module.scss';
import { Mixpanel } from '../../utils/mixpanel';
import { getSubdomain } from '../../utils/utils';
import {
    AICA_BUREAU_KYC_CONSENT_EDIT,
    AICA_ENGAGEMENT_LETTER_EDIT,
    AICA_NDA_EDIT,
    AICA_ONEPAGER_EDIT,
} from '../../utils/constants/mixpanelEvents/investorEvents';
import { TEXT_EDITOR_TYPES } from '../../store/investor/constants';
import { useEditorSync } from './useEditorSync';
import { resetAllSelections } from './Elements/Table/components/tableSelectionUtils';

// --------------------------------------
// Types
// --------------------------------------
interface SlateEditorProps {
    source: string;
    editorInitialData: Descendant[];
    editorContentRef: React.MutableRefObject<Descendant[]>;
    targetRef?: RefObject<HTMLDivElement>;
    assesseeOrgId?: string;
    generating?: boolean;
    onClose?: () => void;
    savingData: boolean;
    editorType: string;
    onOpenHistory?: () => void;
    onToggleHistory?: () => void;
    historyOpen?: boolean;
    readOnly?: boolean;
}

// ✅ PERF: Memoized components (Slate.js official recommendation)
// Prevents unnecessary re-renders when unrelated parts of editor change
const Element: FC<any> = memo((props) => {
    return getBlock(props);
});

const Leaf: FC<any> = memo((props) => {
    let { attributes, children, leaf } = props;
    children = getMarked(leaf, children);
    return <span {...attributes}>{children}</span>;
});

/**
 * Custom normalization plugin to ensure valid Slate structure
 * - Table cells must contain block elements (paragraphs), not direct text
 * - List items must contain block elements (paragraphs), not direct text
 * - Lists must only contain list-items
 */
const withNormalization = (editor: Editor) => {
    const { normalizeNode } = editor;

    editor.normalizeNode = (entry) => {
        const [node, path] = entry;

        // Ensure table cells have paragraph children (not direct text)
        if (SlateElement.isElement(node) && (node.type === 'table-cell' || node.type === 'table-header')) {
            for (const [child, childPath] of Node.children(editor, path)) {
                if (Text.isText(child)) {
                    // Wrap stray text in paragraph
                    Transforms.wrapNodes(editor, { type: 'paragraph', children: [] } as any, { at: childPath });
                    return;
                }
            }
        }

        // Ensure list-items have block children (not direct text)
        if (SlateElement.isElement(node) && node.type === 'list-item') {
            for (const [child, childPath] of Node.children(editor, path)) {
                if (Text.isText(child)) {
                    // Wrap stray text in paragraph
                    Transforms.wrapNodes(editor, { type: 'paragraph', children: [] } as any, { at: childPath });
                    return;
                }
            }
        }

        // Ensure lists only contain list-items (not paragraphs or other blocks directly)
        if (SlateElement.isElement(node) && (node.type === 'orderedList' || node.type === 'unorderedList')) {
            for (const [child, childPath] of Node.children(editor, path)) {
                if (SlateElement.isElement(child) && child.type !== 'list-item') {
                    // Convert non-list-item children to list-items
                    Transforms.wrapNodes(editor, { type: 'list-item', children: [] } as any, { at: childPath });
                    return;
                }
                if (Text.isText(child)) {
                    // Wrap stray text in list-item > paragraph
                    Transforms.wrapNodes(editor, { type: 'paragraph', children: [] } as any, { at: childPath });
                    Transforms.wrapNodes(editor, { type: 'list-item', children: [] } as any, { at: childPath });
                    return;
                }
            }

            // If list is empty, remove it
            if (node.children.length === 0) {
                Transforms.removeNodes(editor, { at: path });
                return;
            }
        }

        // Ensure paragraphs and headings only contain inline content (text nodes and inline elements like links)
        // These should NOT contain other block elements (paragraphs, lists, tables, etc.)
        const inlineOnlyTypes = [
            'paragraph',
            'headingOne',
            'headingTwo',
            'headingThree',
            'headingFour',
            'headingFive',
            'headingSix',
            'blockquote',
            'code',
        ];

        if (SlateElement.isElement(node) && inlineOnlyTypes.includes(node.type)) {
            for (const [child, childPath] of Node.children(editor, path)) {
                if (SlateElement.isElement(child)) {
                    // Check if child is a block element (not inline like link)
                    const inlineTypes = ['link', 'mention'];
                    if (!inlineTypes.includes(child.type)) {
                        // Unwrap the nested block element - lift it to the same level as the parent
                        Transforms.unwrapNodes(editor, { at: path });
                        return;
                    }
                }
            }
        }

        // Ensure tables only contain table-rows
        // Note: We don't add this normalization because it could be too aggressive
        // and delete valid content. Existing validation at deserialization level is sufficient.
        // Tables are constructed programmatically and rarely have invalid children.

        // Ensure table-rows only contain table-cell or table-header
        // Note: Similar to tables, we don't add aggressive normalization here.
        // Table rows are constructed programmatically and validation at creation is sufficient.

        // Ensure links only contain text (not block elements)
        if (SlateElement.isElement(node) && node.type === 'link') {
            for (const [child, childPath] of Node.children(editor, path)) {
                if (SlateElement.isElement(child)) {
                    // Links can't contain block elements - unwrap the link
                    Transforms.unwrapNodes(editor, { at: path });
                    return;
                }
            }
        }

        // Fall back to the original normalizeNode
        normalizeNode(entry);
    };

    return editor;
};

const TextEditor: FC<SlateEditorProps> = ({
    source,
    editorContentRef,
    editorInitialData,
    targetRef,
    assesseeOrgId,
    generating,
    onClose,
    savingData,
    editorType,
    onOpenHistory,
    onToggleHistory,
    historyOpen,
    readOnly = false,
}) => {
    const user = useAppSelector((state) => state.user.userData);

    const scrollPositionRef = useRef(0);
    const preventScrollRef = useRef(false);
    const skipAutoScrollRef = useRef(0); // timestamp: skip scroll if within ~150ms of Enter
    const editor = useMemo(
        () =>
            withNormalization(
                withEquation(
                    withHistory(withEmbeds(withTables(withLists(withExcelPaste(withHtmlPaste(withLinks(withReact(createEditor())))))))),
                ),
            ),
        [],
    );
    const isFirstKeyStrokeRef = useRef(false);

    useEditorSync(editor, editorInitialData);

    const isOnePager = editorType === TEXT_EDITOR_TYPES.ONE_PAGER;

    useEffect(() => {
        const wrapper = targetRef?.current;
        if (!wrapper) return;

        const handleScroll = () => {
            // Use scrollPositionRef.current instead of local variable
            if (preventScrollRef.current && wrapper.scrollTop !== scrollPositionRef.current) {
                wrapper.scrollTop = scrollPositionRef.current;
                preventScrollRef.current = false;
            }
        };

        wrapper.addEventListener('scroll', handleScroll, { passive: false });

        return () => {
            wrapper.removeEventListener('scroll', handleScroll);
        };
    }, [targetRef]);

    // --------------------------------------
    // ✅ Auto-scroll cursor into view (fixed)
    // --------------------------------------
    useEffect(() => {
        // Skip auto-scroll briefly after Enter (prevents upward jump when pressing Enter in lists/lower part)
        const now = Date.now();
        if (skipAutoScrollRef.current > 0 && now - skipAutoScrollRef.current < 150) {
            return;
        }

        if (!editor.selection || !targetRef?.current) return;

        try {
            const domRange = ReactEditor.toDOMRange(editor, editor.selection);
            const rect = domRange.getBoundingClientRect();
            const editorRect = targetRef?.current?.getBoundingClientRect();

            const PADDING = 50;
            const isVisible = rect.top >= editorRect.top + PADDING && rect.bottom <= editorRect.bottom - PADDING;

            if (!isVisible) {
                // Use 'nearest' instead of 'center' to prevent upward scrolling
                domRange.startContainer.parentElement?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest',
                });
            }
        } catch (err) {
            // Selection may not be rendered yet
        }
    }, [editor.selection, targetRef]);

    // Initialize editorContentRef with editorInitialData if it's currently empty.
    useEffect(() => {
        if (editorInitialData?.length && editorContentRef?.current?.length === 0) {
            editorContentRef.current = editorInitialData;
        }
    }, [editorInitialData, editorContentRef]);

    // --------------------------------------
    // Editor change handler
    // --------------------------------------
    const handleEditorChange = useCallback(
        (value: Descendant[]) => {
            // Detect actual changes
            const isAstChange = editor.operations.some((op) => op.type !== 'set_selection');

            // Debug logging in development mode
            if (isAstChange && process.env.NODE_ENV === 'development') {
                const contentOps = editor.operations.filter((op) => op.type !== 'set_selection');
                if (contentOps.length > 0) {
                }
            }

            if (isAstChange && source === 'CREATE_DEAL') {
                // Optionally store new content in localStorage
                // const content = JSON.stringify(value);
                // localStorage.setItem('one_pager_content', content);
                // localStorage.setItem('one_pager_assessee', assesseeOrgId || '');
            }
            editorContentRef.current = value;
        },
        [editor, source, editorContentRef, assesseeOrgId],
    );

    const getMixpanelEditor = (editorType: string) => {
        switch (editorType) {
            case TEXT_EDITOR_TYPES.ONE_PAGER:
                return AICA_ONEPAGER_EDIT;
            case TEXT_EDITOR_TYPES.ENGAGEMENT_LETTER:
                return AICA_ENGAGEMENT_LETTER_EDIT;
            case TEXT_EDITOR_TYPES.BUREAU_KYC_CONSENT:
                return AICA_BUREAU_KYC_CONSENT_EDIT;
            case TEXT_EDITOR_TYPES.NDA:
                return AICA_NDA_EDIT;
            default:
                return '';
        }
    };

    // --------------------------------------
    // Keyboard shortcuts
    // --------------------------------------
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            const { selection } = editor;

            // Special handling for Enter key
            if (event.key === 'Enter') {
                // ✅ FIX: Save scroll position and skip auto-scroll to prevent upward jump
                if (targetRef?.current) {
                    scrollPositionRef.current = targetRef.current.scrollTop;
                    preventScrollRef.current = true;
                    skipAutoScrollRef.current = Date.now();
                    setTimeout(() => {
                        preventScrollRef.current = false;
                    }, 150);
                }
                // Check if at end of list
                const [listItem] = Editor.nodes(editor, {
                    match: (n) => SlateElement.isElement(n) && (n as any).type === 'list-item',
                });

                if (listItem) {
                    const [item, itemPath] = listItem as [any, number[]];
                    // Only get parent if we have a valid path
                    if (itemPath && itemPath.length > 0) {
                        const [list] = Editor.parent(editor, itemPath);
                        const isLastItem = itemPath[itemPath.length - 1] === (list as any).children.length - 1;
                    }
                }
            }

            // Typically Mac's "Command" key is "metaKey" and Windows' Control is `ctrlKey`.
            // Adjust if you want cross-platform behavior, e.g. using event.ctrlKey instead.
            if (event.metaKey) {
                switch (event.key) {
                    case 'b': {
                        event.preventDefault();
                        toggleMark(editor, 'bold');
                        break;
                    }
                    case 'i': {
                        event.preventDefault();
                        toggleMark(editor, 'italic');
                        break;
                    }
                    case 'u': {
                        event.preventDefault();
                        toggleMark(editor, 'underline');
                        break;
                    }
                    case 's': {
                        event.preventDefault();
                        toggleMark(editor, 'strikethrough');
                        break;
                    }
                    default:
                        break;
                }
            }

            if (!isFirstKeyStrokeRef.current) {
                isFirstKeyStrokeRef.current = true;
                const mixpanelEditor = getMixpanelEditor(editorType);

                {
                    mixpanelEditor &&
                        Mixpanel.track(mixpanelEditor, {
                            userRole: user?.accountType,
                            pnoPlatform: getSubdomain(),
                            userId: user?.userId,
                            currentUrl: window.location.href,
                            organizationId: assesseeOrgId,
                        });
                }
            }
        },
        [editor, editorType, user, assesseeOrgId],
    );

    // --------------------------------------
    // Render functions
    // --------------------------------------
    const renderElement = useCallback((props: any) => <Element {...props} />, []);
    const renderLeaf = useCallback((props: any) => {
        const { attributes, children, leaf } = props;

        let style: any = {};

        // Apply font family if specified
        if (leaf.fontFamily) {
            style.fontFamily = leaf.fontFamily;
        }

        return <span {...attributes}>{getMarked(leaf, children)}</span>;
    }, []);

    // --------------------------------------
    // Example HTML code -> text injection (if you have that feature)
    // --------------------------------------
    const [htmlAction, setHtmlAction] = useState<{
        showInput: boolean;
        html: string;
        action: string;
        location: string;
    }>({
        showInput: false,
        html: '',
        action: '',
        location: '',
    });

    const handleCodeToText = (partialState: Partial<typeof htmlAction>) => {
        setHtmlAction((prev) => ({
            ...prev,
            ...partialState,
        }));
    };

    const handleEditorClick = useCallback(
        (event) => {
            if (!event.target.closest('[data-column-selector]') && !event.target.closest('[data-row-selector]')) {
                resetAllSelections(editor);
            }
        },
        [editor],
    );

    return (
        <div className={styles.SlateWrapper}>
            <Slate editor={editor} initialValue={editorInitialData} onChange={handleEditorChange}>
                <Toolbar
                    handleCodeToText={handleCodeToText}
                    onClose={onClose}
                    savingData={savingData}
                    onOpenHistory={onOpenHistory}
                    onToggleHistory={onToggleHistory}
                    historyOpen={historyOpen}
                    readOnly={readOnly}
                />
                {generating ? (
                    <TextEditorShimmerLoader />
                ) : (
                    <div className={styles.EditorWrapper} ref={targetRef} id="element-to-be-downloaded-as-pdf">
                        <Editable
                            placeholder="Write your text here..."
                            renderElement={renderElement}
                            renderLeaf={renderLeaf}
                            onKeyDown={handleKeyDown}
                            spellCheck={true}
                            onClick={handleEditorClick}
                            style={{ outline: 'none' }} // ✅ Remove blue focus border
                            readOnly={readOnly}
                        />
                    </div>
                )}
            </Slate>
        </div>
    );
};

export default TextEditor;
